import { useEffect, useState } from "react";
import {
  Buildings, Envelope, Phone, MapPin, Globe, Palette, ImageSquare, X as XIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  getOrganization,
  normalizeHex,
  TIMEZONE_OPTIONS,
  updateOrganization,
} from "@/lib/admin/organizations";
import MediaBrowser from "@/components/cms/MediaBrowser";

/**
 * Admin · /admin/organizations
 *
 * Organization profile: the basics every admin needs — name, contact,
 * branding. Feature toggles + billing defaults live in /admin/settings.
 */
export default function AdminOrganizations() {
  const { profile } = useAuth();
  const orgId = profile?.organization_id;
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      setLoading(true);
      try {
        const o = await getOrganization(orgId);
        setOrg(o);
        setForm(toForm(o));
      } catch (err) {
        toast.error(err.message || "Could not load organization");
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId]);

  const dirty = org && form && Object.keys(form).some((k) => form[k] !== toForm(org)[k]);

  const onSave = async () => {
    if (!form) return;
    const primary = normalizeHex(form.brand_primary_hex);
    const accent = normalizeHex(form.brand_accent_hex);
    if (!primary) return toast.error("Primary colour must be a valid hex like #5A7E46");
    if (!accent) return toast.error("Accent colour must be a valid hex like #C6851E");
    setSaving(true);
    try {
      const updated = await updateOrganization(orgId, {
        name: form.name.trim(),
        display_name: form.display_name.trim() || form.name.trim(),
        timezone: form.timezone,
        contact_email: form.contact_email.trim(),
        contact_phone: form.contact_phone.trim(),
        contact_address: form.contact_address.trim(),
        logo_url: form.logo_url.trim() || null,
        brand_primary_hex: primary,
        brand_accent_hex: accent,
      });
      setOrg(updated);
      setForm(toForm(updated));
      toast.success("Organization saved");
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  const input =
    "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

  return (
    <div data-testid="admin-organizations" className="space-y-6">
      <section className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Identity
          </p>
          <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
            Organization
          </h1>
          <p className="mt-2 max-w-2xl text-neutral-600">
            Your center's name, contact details, and branding. These appear
            on parent-facing surfaces and invoices.
          </p>
        </div>
        <button
          type="button"
          data-testid="admin-organizations-save"
          disabled={saving || !dirty}
          onClick={onSave}
          className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </section>

      {/* Branding preview header */}
      <section
        data-testid="admin-organizations-preview"
        className="overflow-hidden rounded-2xl border border-neutral-200 bg-white"
      >
        <div
          className="h-24 bg-gradient-to-br"
          style={{
            background: `linear-gradient(135deg, ${form.brand_primary_hex}22, ${form.brand_accent_hex}22)`,
            borderBottom: `2px solid ${form.brand_primary_hex}`,
          }}
        />
        <div className="flex items-center gap-4 px-6 py-4">
          <div
            className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-neutral-200"
            style={{ background: form.brand_primary_hex }}
          >
            {form.logo_url ? (
              <img
                src={form.logo_url}
                alt=""
                aria-label="Organization logo"
                className="h-full w-full object-cover"
              />
            ) : (
              <Buildings size={22} weight="duotone" className="text-white" />
            )}
          </div>
          <div>
            <p className="font-heading text-lg font-semibold tracking-tight text-neutral-900">
              {form.display_name || form.name || "—"}
            </p>
            <p className="text-xs text-neutral-500">
              {form.slug ? `@${form.slug}` : ""}
              {form.slug && form.timezone ? " · " : ""}
              {form.timezone}
            </p>
          </div>
        </div>
      </section>

      {/* Basics */}
      <Section title="Basics" icon={<Buildings size={16} weight="duotone" />}>
        <Row>
          <Field label="Legal name">
            <input
              data-testid="admin-organizations-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={input}
            />
          </Field>
          <Field label="Display name" hint="Shown to parents. Defaults to legal name.">
            <input
              data-testid="admin-organizations-display-name"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              className={input}
            />
          </Field>
        </Row>
        <Row>
          <Field label="URL slug" hint="Fixed — contact support if you need to change it.">
            <input
              data-testid="admin-organizations-slug"
              value={form.slug}
              disabled
              className={`${input} cursor-not-allowed opacity-60`}
            />
          </Field>
          <Field label="Timezone">
            <select
              data-testid="admin-organizations-timezone"
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              className={input}
            >
              {TIMEZONE_OPTIONS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Field>
        </Row>
      </Section>

      {/* Contact */}
      <Section title="Contact" icon={<Globe size={16} weight="duotone" />}>
        <Row>
          <Field label="Email" icon={<Envelope size={12} weight="duotone" />}>
            <input
              data-testid="admin-organizations-contact-email"
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              placeholder="hello@daycare.test"
              className={input}
            />
          </Field>
          <Field label="Phone" icon={<Phone size={12} weight="duotone" />}>
            <input
              data-testid="admin-organizations-contact-phone"
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              placeholder="(555) 123-4567"
              className={input}
            />
          </Field>
        </Row>
        <Field label="Address" icon={<MapPin size={12} weight="duotone" />}>
          <textarea
            data-testid="admin-organizations-contact-address"
            rows={2}
            value={form.contact_address}
            onChange={(e) => setForm({ ...form, contact_address: e.target.value })}
            placeholder="123 Acorn Lane, Anywhere, USA"
            className={input}
          />
        </Field>
      </Section>

      {/* Branding */}
      <Section title="Branding" icon={<Palette size={16} weight="duotone" />}>
        <div className="space-y-3">
          <Field label="Logo URL" icon={<ImageSquare size={12} weight="duotone" />}
                 hint="Pick from the Media library or paste a public URL.">
            <div className="flex gap-2">
              <input
                data-testid="admin-organizations-logo-url"
                value={form.logo_url}
                onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                placeholder="https://…"
                className={input}
              />
              <button
                type="button"
                data-testid="admin-organizations-logo-pick"
                onClick={() => setMediaOpen(true)}
                className="whitespace-nowrap rounded-full border border-primary/40 bg-primary/5 px-3 py-2 text-xs font-semibold text-primary-hover hover:bg-primary/10"
              >
                Pick from media
              </button>
              {form.logo_url && (
                <button
                  type="button"
                  data-testid="admin-organizations-logo-clear"
                  onClick={() => setForm({ ...form, logo_url: "" })}
                  aria-label="Clear logo"
                  className="rounded-full border border-neutral-200 bg-white px-2.5 py-2 text-neutral-400 hover:bg-neutral-50 hover:text-neutral-600"
                >
                  <XIcon size={12} weight="bold" />
                </button>
              )}
            </div>
          </Field>
          <Row>
            <ColorField
              testId="admin-organizations-brand-primary"
              label="Primary colour"
              value={form.brand_primary_hex}
              onChange={(v) => setForm({ ...form, brand_primary_hex: v })}
            />
            <ColorField
              testId="admin-organizations-brand-accent"
              label="Accent colour"
              value={form.brand_accent_hex}
              onChange={(v) => setForm({ ...form, brand_accent_hex: v })}
            />
          </Row>
        </div>
      </Section>

      {mediaOpen && (
        <MediaBrowser
          organizationId={orgId}
          mode="pick"
          onPick={(f, url) => { setForm((prev) => ({ ...prev, logo_url: url })); setMediaOpen(false); }}
          onClose={() => setMediaOpen(false)}
        />
      )}
    </div>
  );
}

function toForm(o) {
  return {
    name: o?.name || "",
    display_name: o?.display_name || o?.name || "",
    slug: o?.slug || "",
    timezone: o?.timezone || "UTC",
    contact_email: o?.contact_email || "",
    contact_phone: o?.contact_phone || "",
    contact_address: o?.contact_address || "",
    logo_url: o?.logo_url || "",
    brand_primary_hex: o?.brand_primary_hex || "#5A7E46",
    brand_accent_hex: o?.brand_accent_hex || "#C6851E",
  };
}

function Section({ title, icon, children }) {
  return (
    <section className="rounded-2xl border border-neutral-200 bg-white p-5">
      <p className="mb-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
        <span className="text-primary-hover">{icon}</span>
        {title}
      </p>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>;
}

function Field({ label, icon, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-neutral-500">
        {icon}
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-[10px] text-neutral-400">{hint}</span>}
    </label>
  );
}

function ColorField({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">
        {label}
      </span>
      <div className="flex items-center gap-2">
        <input
          type="color"
          data-testid={`${testId}-swatch`}
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="h-10 w-14 cursor-pointer rounded-xl border border-neutral-200"
        />
        <input
          data-testid={testId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#5A7E46"
          className="flex-1 rounded-xl border border-neutral-200 bg-white px-3 py-2 font-mono text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      </div>
    </label>
  );
}
